import { apiTimeOffRequest } from "@/api/time-off-request.api.ts";
import { apiTimeOffPolicy } from "@/api/time-off.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiTimeOffCategory,
  ApiTimeOffCategoryPayload,
  ApiTimeOffPolicyAssignToPreviewPayload,
  ApiTimeOffPolicyPayload,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  ApiTimeOffAdjustmentPayload,
  ApiTimeOffCalendarFilter,
  ApiTimeOffRequestSave,
} from "@/types/time-off/time-off.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { timeOffAnalyticsKeys } from "@/queries/analytics/use-time-off-analytics.query.ts";
import { escapeRegExp } from "lodash";

export const timeOffsKeys = createQueryKeys("timeOffs", {
  allCategories: null,
  calendar: (filter: ApiTimeOffCalendarFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiTimeOffPolicy.calendar(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  category: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Category id is required");

      const result = await apiTimeOffPolicy.getCategoryById(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  policy: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Policy id is required");

      const result = await apiTimeOffPolicy.getPolicyById(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  employeePolicyChecks: (employeeId: number, categoryId: number | undefined) => ({
    queryKey: [employeeId, categoryId],
    queryFn: async () => {
      if (categoryId == undefined) throw new ApiPassException("Category id is required");

      const result = await apiTimeOffPolicy.getEmployeePolicyChecks(employeeId, categoryId);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  check: (payload: ApiTimeOffRequestSave | undefined) => ({
    queryKey: ["check", payload],
    queryFn: async () => {
      if (!payload) throw new ApiPassException("Payload is required");

      const result = await apiTimeOffRequest.check(payload);
      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  policyAssignToPreview: (payload: ApiTimeOffPolicyAssignToPreviewPayload) => ({
    queryKey: [payload],
    queryFn: async ({ signal }) => {
      const result = await apiTimeOffPolicy.previewPolicyAssignTo(payload, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  adjustment: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Category id is required");

      const result = await apiTimeOffPolicy.getAdjustmentById(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useTimeOffCategories = ({ enabled = true }: { enabled: boolean }) => {
  return useQuery({
    ...timeOffsKeys.allCategories,
    queryFn: async () => {
      const result = await apiTimeOffPolicy.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useTimeOffCategoriesSearchInline = ({ enabled = true }: { enabled: boolean }) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useTimeOffCategories({ enabled });

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");
    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useTimeOffCalendar = (filter: ApiTimeOffCalendarFilter, enabled: boolean = true) => {
  return useQuery({
    ...timeOffsKeys.calendar(filter),
    enabled: enabled,
  });
};

export const useTimeOffCategoryDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...timeOffsKeys.category(id),
    enabled: enabled && !!id,
  });
};

export const useTimeOffPolicyDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...timeOffsKeys.policy(id),
    enabled: enabled && !!id,
  });
};

export interface TimeOffSaveCategoryParams {
  id: number;
  payload: ApiTimeOffCategoryPayload;
}

export const useTimeOffSaveCategory = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id, payload }: TimeOffSaveCategoryParams) {
      const result =
        id == 0
          ? await apiTimeOffPolicy.create(payload)
          : await apiTimeOffPolicy.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (timeOff) => {
      queryClient.setQueryData(timeOffsKeys.category(timeOff.id).queryKey, timeOff);
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTimeOffDeleteCategory = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ categoryId }: { categoryId: number }) {
      const { success, error } = await apiTimeOffPolicy.deleteCategory(categoryId);

      if (error || !success) throw new ApiErrorException(error);

      return { categoryId };
    },
    onSuccess: ({ categoryId }) => {
      queryClient.setQueryData(timeOffsKeys.category(categoryId).queryKey, undefined);
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export interface TimeOffSavePolicyParams {
  id: number;
  payload: ApiTimeOffPolicyPayload;
}

export const useTimeOffPolicySave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id, payload }: TimeOffSavePolicyParams) {
      const result =
        id == 0
          ? await apiTimeOffPolicy.createPolicy(payload)
          : await apiTimeOffPolicy.updatePolicy(id, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(timeOffsKeys.policy(policy.id).queryKey, policy);
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.category(policy.category_id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export interface TimeOffDeletePolicyParams {
  policyId: number;
  categoryId: number;
}

export const usePolicyDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ policyId, categoryId }: TimeOffDeletePolicyParams) {
      const { success, error } = await apiTimeOffPolicy.deletePolicy(policyId);

      if (error || !success) throw new ApiErrorException(error);

      return { policyId, categoryId };
    },
    onSuccess: ({ policyId, categoryId }) => {
      queryClient.setQueryData(timeOffsKeys.policy(policyId).queryKey, undefined);
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.category(categoryId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffPoliciesSortPreview = (categoryId: number) => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (ids: number[]) {
      const result = await apiTimeOffPolicy.previewPoliciesSort(categoryId, ids);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffPoliciesSort = (categoryId: number) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (ids: number[]) {
      const { success, error } = await apiTimeOffPolicy.sortPolicies(categoryId, ids);

      if (error || !success) throw new ApiErrorException(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.category(categoryId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffPolicyDuplicate = (categoryId: number | undefined) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (policyId: number) {
      const result = await apiTimeOffPolicy.duplicatePolicy(policyId);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.category(categoryId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffPolicyAssignToPreview = (
  payload: ApiTimeOffPolicyAssignToPreviewPayload,
  enabled: boolean
) => {
  return useQuery({
    ...timeOffsKeys.policyAssignToPreview(payload),
    enabled,
  });
};

export const useTimeOffCheck = (
  payload: ApiTimeOffRequestSave | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    ...timeOffsKeys.check(payload),
    enabled:
      enabled &&
      !!payload?.time_off_data.category_id &&
      !!payload?.time_off_data.date_from &&
      !!payload?.time_off_data.date_to,
  });
};

export interface TimeOffAdjustmentParams {
  id?: number;
  payload: ApiTimeOffAdjustmentPayload;
}

export const useTimeOffAdjustment = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: TimeOffAdjustmentParams) => {
      const result = id
        ? await apiTimeOffPolicy.updateAdjustment(id, payload)
        : await apiTimeOffPolicy.createAdjustment(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTimeOffDeleteAdjustment = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiTimeOffPolicy.deleteAdjustment(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys.allCategories.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffsKeys._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      queryClient.invalidateQueries({
        queryKey: timeOffAnalyticsKeys.historyReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffAdjustmentDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...timeOffsKeys.adjustment(id),
    enabled: enabled && !!id,
  });
};

export const useTimeOffCategoriesSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      queryClient.setQueryData(timeOffsKeys.allCategories.queryKey, (old: ApiTimeOffCategory[]) => {
        return ids.map((id: number) => old.find((d: ApiTimeOffCategory) => d.id === id));
      });

      const result = await apiTimeOffPolicy.sort(ids);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffEmployeePolicyChecks = (
  employeeId: number,
  categoryId: number | undefined
) => {
  return useQuery({
    ...timeOffsKeys.employeePolicyChecks(employeeId, categoryId),
    enabled: !!categoryId,
  });
};
